import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { BrandMark } from '@/components/BrandMark';
import { NotificationBell } from '@/components/NotificationBell';
import { DesktopAlertsPrompt } from '@/components/DesktopAlertsPrompt';
import { hasCapability } from '@/context/ProjectContext';
import { useLogoutMutation } from '@/services/api';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { signedOut } from '@/store/authSlice';
import type { CurrentUserDto, ProjectListItemDto } from '@/types/api';


interface Crumb {
  label: string;
  to?: string;
}

export function AppShell({
  project,
  user,
  crumbs,
  children
}: {
  project: ProjectListItemDto | null;
  user: CurrentUserDto;
  crumbs: Crumb[];
  children: React.ReactNode;
}): JSX.Element {
  const sessionExpired = useAppSelector((s) => s.auth.sessionExpired);
  const base = project ? `/p/${project.slug}` : null;
  const canSeeAudit = hasCapability(user, 'audit_log:read');
  const isAdmin = user.role === 'admin';
  const canApprove = user.role === 'admin' || user.role === 'approver';

  return (
    <div className="shell">
      <aside className="sidebar">
        <Link to="/p" className="brand-lockup">
          <BrandMark size={40} />
          <div>
            <div className="brand-name">AI Software Factory</div>
            <div className="brand-tagline">A&nbsp;COGNESENSE&nbsp;BRAND&nbsp;·</div>
          </div>
        </Link>

        <div className="nav-section">All projects</div>
        <NavItem to="/p" label="Projects" />

        {base ? (
          <>
            <ActiveProject project={project!} />
            <NavItem to={`${base}/dashboard`} label="Pipeline runs" />
            <NavItem to={`${base}/new-run`} label="New run" />
            {canSeeAudit && <NavItem to={`${base}/audit-log`} label="Audit log" />}
            <NavItem to={`${base}/settings`} label="Settings" />

            <div className="nav-section">Account</div>
            {canApprove && <NavItem to="/pending-approvals" label="Pending approvals" />}
            <NavItem to="/profile" label="Profile" />
            {isAdmin && <NavItem to="/users" label="Users" />}
            {isAdmin && <NavItem to="/model-configuration" label="Model configuration" />}
            {isAdmin && <NavItem to="/sla-tat" label="SLA & TAT" />}
            <SignOutItem />
          </>
        ) : (
          <>
            <div className="nav-section">Project</div>
            <span className="nav-item" style={{ color: 'var(--text-muted)', cursor: 'default' }}>
              — no project selected —
            </span>
            <div className="nav-section">Account</div>
            {canApprove && <NavItem to="/pending-approvals" label="Pending approvals" />}
            <NavItem to="/profile" label="Profile" />
            {isAdmin && <NavItem to="/users" label="Users" />}
            {isAdmin && <NavItem to="/model-configuration" label="Model configuration" />}
            {isAdmin && <NavItem to="/sla-tat" label="SLA & TAT" />}
            <SignOutItem />
          </>
        )}
      </aside>

      <main className="main">
        <div className="topbar">
          <nav className="crumbs">
            {crumbs.map((c, i) => (
              <span key={`${c.label}-${i}`}>
                {i > 0 && <span className="sep">/</span>}
                {c.to ? (
                  <Link to={c.to}>{c.label}</Link>
                ) : (
                  <strong>{c.label}</strong>
                )}
              </span>
            ))}
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <NotificationBell />
            <UserMenu user={user} />
          </div>
        </div>

        {sessionExpired && <SessionExpiredNotice />}

        <div className="content">{children}</div>
      </main>
      <DesktopAlertsPrompt />
    </div>
  );
}

function NavItem({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <NavLink to={to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} end>
      {label}
    </NavLink>
  );
}

function SignOutItem(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();

  async function onSignOut(): Promise<void> {
    try {
      await logout().unwrap();
    } catch {
    }
    dispatch(signedOut());
    navigate('/login', { replace: true });
  }

  return (
    <button
      type="button"
      className="nav-item"
      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none' }}
      onClick={() => void onSignOut()}
    >
      Sign out
    </button>
  );
}

function ActiveProject({ project }: { project: ProjectListItemDto }): JSX.Element {
  return <div className="nav-section">{project.name}</div>;
}

function UserMenu({ user }: { user: CurrentUserDto }): JSX.Element {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const ref = useRef<HTMLDivElement>(null);
  const initials = (user.name ?? user.email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  useEffect(() => {
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  async function onSignOut(): Promise<void> {
    try {
      await logout().unwrap();
    } catch {
    }
    dispatch(signedOut());
    navigate('/login', { replace: true });
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-chip"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-avatar">{initials}</span>
        <span className="user-name">{user.name ?? user.email}</span>
        <span className="role-pill">{user.role.toUpperCase()}</span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            zIndex: 40,
            minWidth: 220,
            background: 'var(--surface)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)',
            padding: 6
          }}
        >
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{user.name ?? user.email}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {user.email}
            </div>
            <div className="mono-cap" style={{ marginTop: 6 }}>
              {user.role}
            </div>
          </div>
          <Link role="menuitem" to="/profile" className="nav-item" onClick={() => setOpen(false)}>
            Profile
          </Link>
          <button
            role="menuitem"
            type="button"
            className="nav-item"
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none' }}
            onClick={() => void onSignOut()}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function SessionExpiredNotice(): JSX.Element {
  return (
    <div style={{ padding: '12px 32px 0' }}>
      <div className="banner error" role="alert" style={{ marginBottom: 0 }}>
        <span className="banner-icon">!</span>
        <span>
          Your session has expired.{' '}
          <Link to="/login" style={{ borderBottom: '1px solid var(--text)', fontWeight: 500 }}>
            Sign in again
          </Link>{' '}
          to continue — your place is saved.
        </span>
      </div>
    </div>
  );
}
